import { useCallback, useEffect, useRef, useState } from 'react';

import type {
  ContactPresenceActivity,
  ContactPresenceAvailability,
  ContactPresenceViewState,
} from '../components/Chat/ContactPresence.tsx';
import {
  releaseWhatsAppPresence,
  sendWhatsAppPresence,
  subscribeWhatsAppPresence,
  unifiedWebSocketManager,
  type WhatsAppPresenceSignal,
  type WhatsAppPresenceState,
} from '../services/api.ts';

const PRESENCE_CLINIC_ID = 2;
const TYPING_IDLE_MS = 4_000;
const TYPING_REFRESH_MS = 3_000;
const INBOUND_ACTIVITY_TTL_MS = 8_000;
const OBSERVER_RENEW_MS = 20_000;
const SUBSCRIPTION_RETRY_DELAYS_MS = [500, 1_500, 3_000] as const;
const SUBSCRIPTION_SLOW_RETRY_MS = 30_000;

const EMPTY_PRESENCE: ContactPresenceViewState = {
  activity: null,
  availability: null,
  lastSeen: null,
};

const ALLOWED_PRESENCE_STATES = new Set<WhatsAppPresenceState>([
  'offline',
  'online',
  'typing',
  'recording',
  'paused',
]);

const MESSAGE_CONTROL_TYPES = new Set([
  'connection_established',
  'contact_mode_changed',
  'contact_presence',
  'message_reaction',
  'message_status',
  'nps_update',
]);

const createPresenceObserverId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const rand = Math.floor(Math.random() * 16);
    const value = char === 'x' ? rand : (rand & 0x3) | 0x8;
    return value.toString(16);
  });
};

const isDocumentVisible = (): boolean => (
  typeof document === 'undefined' || document.visibilityState !== 'hidden'
);

interface PresenceIdentity {
  contactId: number;
  phone: string;
  generation: number;
}

interface ContactPresenceEvent {
  type: 'contact_presence';
  contact_id: number;
  phone: string;
  state: WhatsAppPresenceState;
  observed_at: string;
  last_seen?: string;
}

interface UseContactPresenceOptions {
  contactId: number | null;
  phone: string | null;
  enabled: boolean;
}

interface UseContactPresenceResult extends ContactPresenceViewState {
  onTextChanged: (value: string) => void;
  onTextBlur: () => void;
  onRecordingStateChanged: (state: 'recording' | 'paused') => void;
  pause: () => void;
}

const normalizePhone = (value: unknown): string =>
  String(value || '').replace(/\D/g, '');

const parseIsoTimestamp = (value: unknown): number | null => {
  if (typeof value !== 'string' || !value.trim()) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const parseContactPresenceEvent = (
  payload: unknown,
  identity: Pick<PresenceIdentity, 'contactId' | 'phone'>,
): ContactPresenceEvent | null => {
  if (!payload || typeof payload !== 'object') return null;

  const event = payload as Record<string, unknown>;
  if (event.type !== 'contact_presence' || 'data' in event) return null;

  const contactId = Number(event.contact_id);
  const eventPhone = normalizePhone(event.phone);
  const state = event.state as WhatsAppPresenceState;
  const observedAt = parseIsoTimestamp(event.observed_at);

  if (
    !Number.isInteger(contactId) ||
    contactId <= 0 ||
    contactId !== identity.contactId ||
    !eventPhone ||
    eventPhone !== identity.phone ||
    !ALLOWED_PRESENCE_STATES.has(state) ||
    observedAt === null
  ) {
    return null;
  }

  const lastSeen = parseIsoTimestamp(event.last_seen) === null
    ? undefined
    : String(event.last_seen);

  return {
    type: 'contact_presence',
    contact_id: contactId,
    phone: eventPhone,
    state,
    observed_at: String(event.observed_at),
    ...(lastSeen ? { last_seen: lastSeen } : {}),
  };
};

export const useContactPresence = ({
  contactId,
  phone,
  enabled,
}: UseContactPresenceOptions): UseContactPresenceResult => {
  const boundContactId = Number(contactId);
  const boundPhone = normalizePhone(phone);
  const [presence, setPresence] = useState<ContactPresenceViewState>(EMPTY_PRESENCE);
  const identityRef = useRef<PresenceIdentity | null>(null);
  const generationRef = useRef(0);
  const capabilityRef = useRef(false);
  const inboundEventCountRef = useRef(0);
  const lastObservedAtRef = useRef(Number.NEGATIVE_INFINITY);
  const activityTokenRef = useRef(0);
  const activityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typingIdleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const outboundStateRef = useRef<WhatsAppPresenceSignal | null>(null);
  const lastTypingSentAtRef = useRef(0);
  const outboundQueueRef = useRef<Promise<void>>(Promise.resolve());

  const clearActivityTimer = useCallback(() => {
    if (activityTimerRef.current) {
      clearTimeout(activityTimerRef.current);
      activityTimerRef.current = null;
    }
  }, []);

  const clearTypingIdleTimer = useCallback(() => {
    if (typingIdleTimerRef.current) {
      clearTimeout(typingIdleTimerRef.current);
      typingIdleTimerRef.current = null;
    }
  }, []);

  const clearInboundActivity = useCallback(() => {
    activityTokenRef.current += 1;
    clearActivityTimer();
    setPresence((current) => (
      current.activity === null
        ? current
        : { ...current, activity: null }
    ));
  }, [clearActivityTimer]);

  const clearPresenceView = useCallback(() => {
    activityTokenRef.current += 1;
    clearActivityTimer();
    setPresence(EMPTY_PRESENCE);
  }, [clearActivityTimer]);

  const enqueuePresenceSignal = useCallback((
    target: PresenceIdentity,
    signal: WhatsAppPresenceSignal,
  ) => {
    outboundQueueRef.current = outboundQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        if (Number(localStorage.getItem('clinic_id')) !== PRESENCE_CLINIC_ID) {
          return;
        }

        const result = await sendWhatsAppPresence(target.contactId, signal);
        const currentIdentity = identityRef.current;
        if (
          currentIdentity?.generation === target.generation &&
          result.enabled === false
        ) {
          capabilityRef.current = false;
        }
      })
      .catch(() => undefined);
  }, []);

  const getBoundIdentity = useCallback((): PresenceIdentity | null => {
    const identity = identityRef.current;
    if (
      !identity ||
      identity.contactId !== boundContactId ||
      identity.phone !== boundPhone
    ) {
      return null;
    }
    return identity;
  }, [boundContactId, boundPhone]);

  const pause = useCallback(() => {
    clearTypingIdleTimer();
    const identity = getBoundIdentity();
    if (
      !identity ||
      !capabilityRef.current ||
      outboundStateRef.current === null
    ) {
      outboundStateRef.current = null;
      lastTypingSentAtRef.current = 0;
      return;
    }

    outboundStateRef.current = null;
    lastTypingSentAtRef.current = 0;
    enqueuePresenceSignal(identity, 'paused');
  }, [clearTypingIdleTimer, enqueuePresenceSignal, getBoundIdentity]);

  const onTextChanged = useCallback((value: string) => {
    const identity = getBoundIdentity();
    if (!identity) return;

    if (!value.trim()) {
      pause();
      return;
    }

    if (outboundStateRef.current === 'recording') return;

    const now = Date.now();
    if (
      outboundStateRef.current !== 'typing' ||
      now - lastTypingSentAtRef.current >= TYPING_REFRESH_MS
    ) {
      outboundStateRef.current = 'typing';
      lastTypingSentAtRef.current = now;
      if (capabilityRef.current) {
        enqueuePresenceSignal(identity, 'typing');
      }
    }

    clearTypingIdleTimer();
    const generation = identity.generation;
    typingIdleTimerRef.current = setTimeout(() => {
      if (identityRef.current?.generation === generation) {
        pause();
      }
    }, TYPING_IDLE_MS);
  }, [clearTypingIdleTimer, enqueuePresenceSignal, getBoundIdentity, pause]);

  const onRecordingStateChanged = useCallback((state: 'recording' | 'paused') => {
    const identity = getBoundIdentity();
    if (!identity) return;

    if (state === 'paused') {
      pause();
      return;
    }

    clearTypingIdleTimer();
    if (outboundStateRef.current === 'recording') return;
    outboundStateRef.current = 'recording';
    lastTypingSentAtRef.current = 0;
    if (capabilityRef.current) {
      enqueuePresenceSignal(identity, 'recording');
    }
  }, [clearTypingIdleTimer, enqueuePresenceSignal, getBoundIdentity, pause]);

  const applyPresenceState = useCallback((
    state: WhatsAppPresenceState,
    lastSeen: string | null,
  ) => {
    const nextActivity: ContactPresenceActivity =
      state === 'typing' || state === 'recording' ? state : null;
    const nextAvailability: ContactPresenceAvailability =
      state === 'online' || state === 'offline' ? state : null;

    if (nextActivity) {
      activityTokenRef.current += 1;
      const token = activityTokenRef.current;
      clearActivityTimer();
      activityTimerRef.current = setTimeout(() => {
        if (activityTokenRef.current === token) {
          setPresence((current) => ({ ...current, activity: null }));
          activityTimerRef.current = null;
        }
      }, INBOUND_ACTIVITY_TTL_MS);
    } else {
      activityTokenRef.current += 1;
      clearActivityTimer();
    }

    setPresence((current) => ({
      activity: nextActivity,
      availability: nextAvailability ?? current.availability,
      lastSeen: lastSeen ?? current.lastSeen,
    }));
  }, [clearActivityTimer]);

  useEffect(() => {
    generationRef.current += 1;
    const generation = generationRef.current;
    const normalizedPhone = boundPhone;
    const currentClinicId = Number(localStorage.getItem('clinic_id'));
    const normalizedContactId = boundContactId;

    clearTypingIdleTimer();
    clearPresenceView();
    capabilityRef.current = false;
    inboundEventCountRef.current = 0;
    lastObservedAtRef.current = Number.NEGATIVE_INFINITY;
    outboundStateRef.current = null;
    lastTypingSentAtRef.current = 0;
    identityRef.current = null;

    if (
      !enabled ||
      currentClinicId !== PRESENCE_CLINIC_ID ||
      !Number.isInteger(normalizedContactId) ||
      normalizedContactId <= 0 ||
      !normalizedPhone
    ) {
      return;
    }

    const identity: PresenceIdentity = {
      contactId: normalizedContactId,
      phone: normalizedPhone,
      generation,
    };
    identityRef.current = identity;
    const observerId = createPresenceObserverId();
    let disposed = false;
    let hasConnected = false;
    let needsResubscribe = false;
    let connectionOpen = false;
    let observerAuthorized = false;
    let subscriptionAttempt = 0;
    let subscriptionRetryIndex = 0;
    let subscriptionRetryTimer: ReturnType<typeof setTimeout> | null = null;
    let observerRenewTimer: ReturnType<typeof setInterval> | null = null;

    const clearSubscriptionRetry = () => {
      if (subscriptionRetryTimer !== null) {
        clearTimeout(subscriptionRetryTimer);
        subscriptionRetryTimer = null;
      }
    };

    const clearObserverRenew = () => {
      if (observerRenewTimer !== null) {
        clearInterval(observerRenewTimer);
        observerRenewTimer = null;
      }
    };

    const startObserverRenew = () => {
      if (observerRenewTimer !== null || disposed) return;
      observerRenewTimer = setInterval(() => {
        requestPresenceSubscription();
      }, OBSERVER_RENEW_MS);
    };

    const releaseObserverAuthorization = () => {
      clearObserverRenew();
      if (!observerAuthorized) return;
      observerAuthorized = false;
      void releaseWhatsAppPresence(identity.contactId, observerId).catch(() => undefined);
    };

    const isPresenceScopeActive = () => (
      Number(localStorage.getItem('clinic_id')) === PRESENCE_CLINIC_ID
    );

    const isCurrentSubscription = (attempt: number) => (
      !disposed &&
      attempt === subscriptionAttempt &&
      connectionOpen &&
      isPresenceScopeActive() &&
      identityRef.current?.generation === generation
    );

    function schedulePresenceSubscriptionRetry() {
      if (
        disposed ||
        !connectionOpen ||
        !isPresenceScopeActive() ||
        identityRef.current?.generation !== generation ||
        subscriptionRetryTimer !== null
      ) {
        return;
      }

      const delay = SUBSCRIPTION_RETRY_DELAYS_MS[subscriptionRetryIndex]
        ?? SUBSCRIPTION_SLOW_RETRY_MS;
      if (subscriptionRetryIndex < SUBSCRIPTION_RETRY_DELAYS_MS.length) {
        subscriptionRetryIndex += 1;
      }
      subscriptionRetryTimer = setTimeout(() => {
        subscriptionRetryTimer = null;
        requestPresenceSubscription();
      }, delay);
    }

    const handleMessage = (payload: unknown) => {
      if (
        !isPresenceScopeActive() ||
        identityRef.current?.generation !== generation
      ) {
        return;
      }

      const event = parseContactPresenceEvent(payload, identity);
      if (event) {
        const observedAt = Date.parse(event.observed_at);
        if (observedAt < lastObservedAtRef.current) return;

        lastObservedAtRef.current = observedAt;
        inboundEventCountRef.current += 1;
        applyPresenceState(event.state, event.last_seen || null);
        return;
      }

      if (!payload || typeof payload !== 'object') return;
      const message = payload as Record<string, unknown>;
      if (
        message.type === 'contact_presence' ||
        MESSAGE_CONTROL_TYPES.has(String(message.type || '')) ||
        message.fromMe !== false ||
        normalizePhone(message.phone) !== identity.phone
      ) {
        return;
      }

      clearInboundActivity();
    };

    function requestPresenceSubscription() {
      if (
        disposed ||
        !connectionOpen ||
        !isDocumentVisible() ||
        !isPresenceScopeActive() ||
        identityRef.current?.generation !== generation
      ) {
        return;
      }

      clearSubscriptionRetry();
      const attempt = ++subscriptionAttempt;
      const eventCountAtSubscription = inboundEventCountRef.current;

      void subscribeWhatsAppPresence(identity.contactId, observerId)
        .then((response) => {
          if (!isCurrentSubscription(attempt)) {
            return;
          }

          capabilityRef.current = response.supported === true;
          if (!capabilityRef.current) {
            clearTypingIdleTimer();
            outboundStateRef.current = null;
            lastTypingSentAtRef.current = 0;
            return;
          }

          const desiredOutboundState = outboundStateRef.current;
          if (desiredOutboundState) {
            enqueuePresenceSignal(identity, desiredOutboundState);
          }

          const receivedLiveEvent = (
            inboundEventCountRef.current !== eventCountAtSubscription
          );

          if (!receivedLiveEvent) {
            const snapshotState = response.presence;
            if (snapshotState && ALLOWED_PRESENCE_STATES.has(snapshotState)) {
              const lastSeen = parseIsoTimestamp(response.last_seen) === null
                ? null
                : response.last_seen;
              applyPresenceState(snapshotState, lastSeen);
            }
          }

          if (response.subscribed === true) {
            observerAuthorized = true;
            clearSubscriptionRetry();
            subscriptionRetryIndex = 0;
            startObserverRenew();
            return;
          }

          observerAuthorized = false;
          clearObserverRenew();
          schedulePresenceSubscriptionRetry();
        })
        .catch(() => {
          if (isCurrentSubscription(attempt)) {
            schedulePresenceSubscriptionRetry();
          }
        });
    }

    const handleVisibilityChange = () => {
      if (identityRef.current?.generation !== generation) return;
      if (!isDocumentVisible()) {
        clearSubscriptionRetry();
        releaseObserverAuthorization();
        return;
      }
      if (connectionOpen) {
        subscriptionRetryIndex = 0;
        requestPresenceSubscription();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    unifiedWebSocketManager.subscribe(identity.phone);
    const unsubscribeMessages = unifiedWebSocketManager.onMessage('*', handleMessage);
    const unsubscribeConnection = unifiedWebSocketManager.onConnectionStatus((connected) => {
      if (identityRef.current?.generation !== generation) return;

      if (!connected) {
        connectionOpen = false;
        subscriptionAttempt += 1;
        clearSubscriptionRetry();
        subscriptionRetryIndex = 0;
        releaseObserverAuthorization();
        if (hasConnected) {
          needsResubscribe = true;
        }
        clearPresenceView();
        return;
      }

      connectionOpen = true;
      if (!hasConnected) {
        hasConnected = true;
        subscriptionRetryIndex = 0;
        requestPresenceSubscription();
        return;
      }
      if (needsResubscribe) {
        needsResubscribe = false;
        subscriptionRetryIndex = 0;
        requestPresenceSubscription();
      }
    });

    return () => {
      disposed = true;
      subscriptionAttempt += 1;
      clearSubscriptionRetry();
      clearObserverRenew();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      unsubscribeMessages();
      unsubscribeConnection();
      unifiedWebSocketManager.unsubscribe(identity.phone);
      clearTypingIdleTimer();
      clearActivityTimer();
      releaseObserverAuthorization();

      if (capabilityRef.current && outboundStateRef.current !== null) {
        enqueuePresenceSignal(identity, 'paused');
      }

      if (identityRef.current?.generation === generation) {
        identityRef.current = null;
      }
      capabilityRef.current = false;
      outboundStateRef.current = null;
      lastTypingSentAtRef.current = 0;
    };
  }, [
    applyPresenceState,
    clearActivityTimer,
    clearInboundActivity,
    clearPresenceView,
    clearTypingIdleTimer,
    boundContactId,
    boundPhone,
    enabled,
    enqueuePresenceSignal,
  ]);

  return {
    ...presence,
    onTextChanged,
    onTextBlur: pause,
    onRecordingStateChanged,
    pause,
  };
};
